import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { ApiError } from "@/lib/apiClient";
import {
  approvalStatusClass,
  approvalStatusLabel,
  assignInvoiceApproval,
  type InvoiceApprovalApprover,
} from "@/lib/invoiceApproval";
import type { InvoiceMailCandidate } from "@/lib/invoiceMail";
import { toast } from "@/lib/toast";

export function AssignInvoiceControl({
  row,
  approvers,
  onAssigned,
}: {
  row: InvoiceMailCandidate;
  approvers: InvoiceApprovalApprover[];
  onAssigned: (candidateId: number, assigneeLogin: string, assigneeName: string, status: string) => void;
}) {
  const suggestion = row.assignment_suggestion;
  const approval = row.approval;
  const locked =
    approval?.status === "freigegeben" ||
    approval?.status === "abgelehnt" ||
    approval?.status === "warte_zweite_freigabe";
  const [login, setLogin] = useState(approval?.assignee_login || suggestion?.login || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLogin(approval?.assignee_login || suggestion?.login || "");
  }, [row.id, approval?.assignee_login, suggestion?.login]);

  async function assign() {
    if (!login) {
      toast.error("Bitte einen Mitarbeiter aus dem Büro wählen.");
      return;
    }
    setSaving(true);
    try {
      const detail = await assignInvoiceApproval(row.id, login);
      const name = detail.approval.assignee_name || login;
      onAssigned(row.id, detail.approval.assignee_login || login, name, detail.approval.status);
      toast.success(`Zur Freigabe an ${name} zugewiesen.`);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Zuweisen fehlgeschlagen.";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="space-y-2 border-t border-separator px-4 py-3"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="flex flex-wrap items-center gap-2">
        {approval ? (
          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${approvalStatusClass(approval.status)}`}>
            {approvalStatusLabel(approval.status)}
            {approval.assignee_name ? ` · ${approval.assignee_name}` : ""}
          </span>
        ) : (
          <span className="text-xs font-medium text-label-secondary">Noch niemand zugewiesen</span>
        )}
        {approval ? (
          <Link to="/bestellwesen/rechnungsfreigabe" className="text-xs text-accent hover:underline">
            Zur Rechnungsfreigabe
          </Link>
        ) : null}
      </div>

      {suggestion?.reason ? (
        <p className={`text-xs ${suggestion.login ? "text-label-secondary" : "text-status-orange"}`}>
          {suggestion.login && suggestion.name ? (
            <span className="font-medium text-label">Vorschlag: {suggestion.name}. </span>
          ) : null}
          {suggestion.reason}
        </p>
      ) : null}

      {locked ? null : (
        <div className="flex flex-wrap items-center gap-2">
          <label className="text-xs text-label-secondary">
            Freigabe an
            <select
              className="ml-2 rounded border border-separator bg-card px-2 py-1 text-sm text-label"
              value={login}
              onChange={(event) => setLogin(event.target.value)}
            >
              <option value="">Mitarbeiter wählen</option>
              {approvers.map((person) => (
                <option key={person.login} value={person.login}>
                  {person.name}
                  {person.initials ? ` (${person.initials})` : ""}
                </option>
              ))}
            </select>
          </label>
          <Button size="sm" variant="secondary" disabled={saving || !login} onClick={() => void assign()}>
            {saving ? "Weist zu…" : approval ? "Neu zuweisen" : "Zur Freigabe zuweisen"}
          </Button>
        </div>
      )}
    </div>
  );
}
